import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import MemberIncentiveDashboard from '@/components/incentives/MemberIncentiveDashboard';
import MemberCardPortal from '@/components/payments/MemberCardPortal';
import MemberPayoutHistory from '@/components/financial/MemberPayoutHistory';
import { getMemberData } from '@/lib/memberAuth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useSubtabNavigation } from '@/hooks/useSubtabNavigation';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';

import { useAppTranslation } from '@/contexts/TranslationContext';
/**
 * Member Incentive Dashboard Page
 * Member-facing view of earned incentives, points, achievements, card, and payouts
 */
export default function MemberIncentivesPage() {
  const { t } = useAppTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [memberId, setMemberId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  useSubtabNavigation(setActiveTab, ['overview', 'card', 'payouts'] as const);

  useEffect(() => {
    // Check if user is logged in as organization/staff instead of member
    const orgToken = localStorage.getItem('auth_token');
    const memberData = getMemberData();

    if (orgToken && !memberData) {
      // Organization user trying to access member portal
      alert('This page is for members only. Please use Card Management from the admin panel to manage member cards.');
      navigate('/organizations');
      return;
    }

    // Try to get member ID from auth context first
    if (memberData?.id) {
      setMemberId(memberData.id);
    } else {
      // Fallback to query params
      const memberIdParam = searchParams.get('memberId');
      if (memberIdParam) {
        setMemberId(parseInt(memberIdParam));
      } else {
        // For demo/testing - use member ID 1
        // In production, get from auth context or session
        setMemberId(1);
      }
    }
  }, [searchParams, navigate]);

  const header = (
    <PageHeader
      leading={
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/member/dashboard')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('incentives.back_to_dashboard', 'Back to Dashboard')}
        </Button>
      }
      title={t('incentives.my_incentives_rewards', 'My Incentives & Rewards')}
      description={t('incentives.track_your_earned_rewards_points', 'Track your earned rewards, points, achievements, prepaid card, and payouts')}
    />
  );

  if (!memberId) {
    return (
      <>
        {header}
        <PageContainer size="wide">
          <PageState
            variant="loading"
            message={t('incentives.loading_incentive_dashboard', 'Loading incentive dashboard...')}
          />
        </PageContainer>
      </>
    );
  }

  return (
    <>
      {header}
      <PageContainer size="wide">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">{t('incentives.overview', 'Overview')}</TabsTrigger>
            <TabsTrigger value="card">{t('incentives.my_card', 'My Card')}</TabsTrigger>
            <TabsTrigger value="payouts">{t('incentives.payment_history', 'Payment History')}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <MemberIncentiveDashboard memberId={memberId} />
          </TabsContent>

          <TabsContent value="card">
            <MemberCardPortal memberId={memberId} />
          </TabsContent>

          <TabsContent value="payouts">
            <MemberPayoutHistory memberId={memberId} />
          </TabsContent>
        </Tabs>
      </PageContainer>
    </>
  );
}
